import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, AfterViewChecked, OnDestroy } from '@angular/core';
import {
  Router,
  ActivatedRoute,
  NavigationEnd,
  Params,
  PRIMARY_OUTLET,
  NavigationStart,
  NavigationCancel,
} from '@angular/router';
import { filter, tap } from 'rxjs/operators';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ProjectService } from 'src/app/data/project/project.service';
import { SyncService } from '../../../app/api/sync.service';

interface BreadcrumbInterface {
  label: string;
  params: Params;
  queryParams: Params;
  url: string;
}

@Component({
    selector: 'app-breadcrumb',
    templateUrl: './breadcrumb.component.html',
    styleUrls: ['./breadcrumb.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BreadcrumbComponent implements OnInit, AfterViewChecked, OnDestroy {
  public breadcrumbs: BreadcrumbInterface[];
  showSpinnerSubject$ = new BehaviorSubject<boolean>(false);
  showSpinnerAction$ = this.showSpinnerSubject$.asObservable();
  navigationEvent = this.router.events;
  navigationEventEnd = this.navigationEvent.pipe(
    filter(
      (event) =>
        event instanceof NavigationEnd || event instanceof NavigationCancel
    ),
    tap(() => this.showSpinnerSubject$.next(false)),
    tap(() => {
      this.breadcrumbs = this.getBreadcrumbs(this.activatedRoute.root);
    })
  );
  navigationEventStart = this.navigationEvent.pipe(
    filter((event) => event instanceof NavigationStart),
    tap(() => this.showSpinnerSubject$.next(true))
  );
  subscriptions: Subscription[] = [];
  projectTitle: string;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private projectService: ProjectService,
    private syncService: SyncService,
    private cdr: ChangeDetectorRef) {
    this.breadcrumbs = [];
  }

  ngOnInit(): void {
    this.navigationEventStart.subscribe();
    this.navigationEventEnd.subscribe();
    this.syncService.updateProjectTitleBreadcrumb.subscribe(isProjectTitleUpdated => {
      if (isProjectTitleUpdated)
        this.breadcrumbs = this.getBreadcrumbs(this.activatedRoute.root);
    });
    this.subscriptions.push(this.projectService.projectTitle$.subscribe(value => {
      this.projectTitle = value;
    }));
  }

  private getBreadcrumbs(
    route: ActivatedRoute,
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types
    url: string = '',
    breadcrumbs: BreadcrumbInterface[] = []
  ): BreadcrumbInterface[] {
    const routeDataBreadcrumb = 'breadcrumb';
    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      if (child.outlet !== PRIMARY_OUTLET) {
        continue;
      }

      // eslint-disable-next-line no-prototype-builtins
      if (!child.snapshot.data.hasOwnProperty(routeDataBreadcrumb)) {
        return this.getBreadcrumbs(child, url, breadcrumbs);
      }

      const routeURL: string = child.snapshot.url
        .map((segment) => segment.path)
        .join('/');

      url += `/${routeURL}`;

      const breadcrumb: BreadcrumbInterface = {
        label: child.snapshot.data[routeDataBreadcrumb],
        params: child.snapshot.params,
        queryParams: child.snapshot.queryParams,
        url,
      };
      if (breadcrumb.label) {
        
        if (breadcrumb.label === "Project") {
          this.subscriptions.push(this.projectService.projectTitle$.subscribe(value => {
            this.projectTitle = value;
            breadcrumb.label = value ? value : '';
          }));
          breadcrumb.label = this.projectTitle ? this.projectTitle : '';

        } else if (breadcrumb.label === "Line Items") {
          this.subscriptions.push(
            this.syncService.jeHeaderNameSubject.asObservable().subscribe((name) => {
              breadcrumb.label = name;
            })
          );
        } else if (breadcrumb.label === "Mapping Name") {
          this.subscriptions.push(
            this.syncService.mappingNameSubject.asObservable().subscribe((name) => {
              breadcrumb.label = name;
            })
          );
        } else if (breadcrumb.label === 'Impact Analysis' && breadcrumb.url.endsWith('/impact-analysis')) {
          this.subscriptions.push(
            this.syncService.jeHeaderNameSubject.asObservable().subscribe((name) => {
              breadcrumb.label = 'Journal Entry Impact Analysis';
            })
          );

          const lineItemsBreadcrumb: BreadcrumbInterface = {
            label: 'Line Items',
            params: child.snapshot.params,
            queryParams: {},
            url: breadcrumb.url.replace('/impact-analysis', '/line-items'),
          };

          this.subscriptions.push(
            this.syncService.jeHeaderNameSubject.asObservable().subscribe((name) => {
              lineItemsBreadcrumb.label = name || 'Line Items';
            })
          );

          breadcrumbs.push(lineItemsBreadcrumb);
        }
        breadcrumbs.push(breadcrumb);
      }
      if (breadcrumb.label === "") {
        const urlArray = breadcrumb.url.split('/');
        if (urlArray.length === 3 && urlArray[1] === 'project') {
          breadcrumb.label = this.projectTitle;
        }
      }
      return this.getBreadcrumbs(child, url, breadcrumbs);
    }
    return [];
  }

  ngAfterViewChecked(): void {
    this.cdr.detectChanges();
  }

  goFullScreen(): void {
    (document.querySelector('#main') as HTMLElement).requestFullscreen().then();
  }

  ngOnDestroy(): void {
    // prevent memory leak when component destroyed
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
